import {
  type To
} from 'react-router-dom';
import {
  type RouterNavigateOptions
} from '@remix-run/router';
import {
  type Middleware
} from '@reduxjs/toolkit';
import {
  OmegaApiResponseStatusEnum
} from '@shared/api';
import {
  resetStore
} from '@shared/lib';
import {
  featureToggles
} from '@shared/config';
import {
  reportLog,
  pushToGtm,
} from '@shared/analytics';
import {
  signOut,
  sessionSlice,
  type SessionState,
} from '@entities/session';

type HandleNotLoggedInErrorMiddlewareOptions = {
  navigate: (to: To | null, opts?: RouterNavigateOptions) => Promise<void>;
};

export const handleNotLoggedInErrorMiddleware = (options?: HandleNotLoggedInErrorMiddlewareOptions): Middleware =>
  ({ dispatch, getState }) => next => (action: unknown) => {
    const responseStatus = (action as { payload: { status: OmegaApiResponseStatusEnum } })?.payload?.status;

    if (responseStatus === OmegaApiResponseStatusEnum.NotLoggedIn) {
      const pushToGtmPromise = pushToGtm({
        dataLayer: {
          event: 'app_error',
          errorType: 'AuthError',
          errorCode: 'NotLoggedIn',
          errorMessage: '401: NotLoggedIn handled by middleware.',
          pagePath: window.location.pathname,
          pageTitle: document.title,
          timestamp: Date.now(),
        },
      });
      pushToGtmPromise.then();

      const state = getState() as unknown as { [sessionSlice.name]: SessionState };
      const reportLogPromise = reportLog(state[sessionSlice.name].sessionKey, {
        kind: 'error',
        type: 'handle-not-logged-in-error',
        user: {
          partyId: state[sessionSlice.name].shortProfile?.partyId
        },
        payload: {
          errorType: 'AuthError',
          errorCode: 'NotLoggedIn',
          errorMessage: '401: NotLoggedIn handled by middleware.',
        },
      });
      reportLogPromise.then();

      if (featureToggles.handleAuthErrors) {
        const currentLocation = window.location;
        const currentPath = currentLocation.pathname;
        const searchParams = currentLocation.search;

        if (currentPath !== '/login') {
          const fullPath = `${currentPath}${searchParams}`;
          sessionStorage.setItem('historyPath', fullPath);
        }

        dispatch(signOut());
        dispatch(resetStore());

        options?.navigate('/login');
      }
    }

    return next(action);
  };
